/**
 * RTK Query API configuration
 * Pattern matches restohub-frontend admin
 */

import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { createEndpoints } from './endpointGenerator'
import { CACHE_TAGS } from './endpointsConfig'
import { getEnvironmentConfig } from '@/config/environment'
import { auth } from '@/config/firebase'

/**
 * Base query with Firebase authentication support
 * Public endpoints (menu) work without auth
 * Checkout/orders/payments require Firebase token
 */
const baseQuery = fetchBaseQuery({
  baseUrl: getEnvironmentConfig().apiBaseUrl,
  fetchFn: (input, init) => fetch(input, { ...init, cache: 'no-store' }),
  prepareHeaders: async (headers, { endpoint }) => {
    // Add Firebase auth token if user is signed in
    const user = auth.currentUser
    if (user) {
      try {
        const idToken = await user.getIdToken()
        headers.set('Authorization', `Bearer ${idToken}`)
      } catch (error) {
        console.error('[API] Error getting Firebase ID token:', error)
      }
    }

    // Send X-Restaurant-Id header so the backend treats anonymous users
    // as clients (creates ClientProfile) instead of staff (triggers onboarding)
    const { restaurantId } = getEnvironmentConfig()
    if (restaurantId) {
      headers.set('X-Restaurant-Id', restaurantId)
    }

    if (!endpoint?.includes('upload') && !endpoint?.includes('image')) {
      headers.set('Content-Type', 'application/json')
    }

    return headers
  },
})

/**
 * Base query wrapper that handles auth errors
 * For widget: allows unauthenticated requests (public menu)
 * Auth required only for checkout/orders/payments
 */
const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    console.error('[API] Authentication failed:', result.error)
  }

  return result
}

/**
 * Main API instance
 * All endpoints are generated from endpointsConfig.ts
 */
export const api = createApi({
  reducerPath: 'menuWidgetApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [...CACHE_TAGS],
  endpoints: (builder) => ({
    ...createEndpoints(builder),
  }),
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: 60,
})

// Hooks are generated automatically from endpointsConfig.ts
// Access via convenienceAccessor.ts: menuWidgetApi.menuConfig.get()
